export interface ThumbnailMetadata {
  position: number;
  width: number;
  height: number;
  timeInSeconds: number;
}

export interface VideoMetadata {
  duration: number;
  width: number;
  height: number;
}

export interface VideoSelection {
  startTime: number;
  endTime: number;
  duration: number;
}

export const MAX_SELECTION_DURATION = 5;
export const FIXED_THUMBNAIL_WIDTH = 100;
export const THUMBNAIL_GAP = 0; // Gap between thumbnails

export function calculateThumbnails(
  videoMetadata: VideoMetadata,
  stripHeight: number,
  thumbnailsPerSecond: number = 0.5 // One thumbnail every 2 seconds
): ThumbnailMetadata[] {
  // For short videos, increase thumbnail density to fill space better
  // For videos under 10 seconds, use 1 thumbnail per second
  // For videos under 20 seconds, use 0.75 thumbnails per second
  let adjustedThumbnailsPerSecond = thumbnailsPerSecond;
  if (videoMetadata.duration <= 10) {
    adjustedThumbnailsPerSecond = 1.0; // 1 per second
  } else if (videoMetadata.duration <= 20) {
    adjustedThumbnailsPerSecond = 0.75; // 1 every 1.33 seconds
  }

  const thumbnailInterval = 1 / adjustedThumbnailsPerSecond;
  const thumbnailCount = Math.ceil(videoMetadata.duration / thumbnailInterval);

  const aspectRatio = videoMetadata.width / videoMetadata.height;
  const thumbnailHeight = Math.min(
    stripHeight,
    FIXED_THUMBNAIL_WIDTH / aspectRatio
  );
  const thumbnailWidth = FIXED_THUMBNAIL_WIDTH;

  const thumbnails: ThumbnailMetadata[] = [];

  for (let i = 0; i < thumbnailCount; i++) {
    const timeInSeconds = i * thumbnailInterval;
    thumbnails.push({
      position: i * (thumbnailWidth + THUMBNAIL_GAP),
      width: thumbnailWidth,
      height: thumbnailHeight,
      timeInSeconds,
    });
  }
  return thumbnails;
}

export function timeToPixels(
  timeInSeconds: number,
  duration: number,
  stripWidth: number
): number {
  return (timeInSeconds / duration) * stripWidth;
}

export function pixelsToTime(
  pixels: number,
  duration: number,
  stripWidth: number
): number {
  return (pixels / stripWidth) * duration;
}

export function formatTime(timeInSeconds: number): string {
  const minutes = Math.floor(timeInSeconds / 60);
  const seconds = Math.floor(timeInSeconds % 60);
  return `${minutes.toString().padStart(2, '0')}:${seconds
    .toString()
    .padStart(2, '0')}`;
}

export function constrainSelection(
  startTime: number,
  endTime: number,
  maxDuration: number,
  videoDuration: number
): VideoSelection {
  let constrainedStart = Math.max(0, startTime);
  let constrainedEnd = Math.min(videoDuration, endTime);

  const selectionDuration = constrainedEnd - constrainedStart;

  if (selectionDuration > maxDuration) {
    if (constrainedStart + maxDuration <= videoDuration) {
      constrainedEnd = constrainedStart + maxDuration;
    } else {
      constrainedStart = constrainedEnd - maxDuration;
    }
  }

  return {
    startTime: constrainedStart,
    endTime: constrainedEnd,
    duration: constrainedEnd - constrainedStart,
  };
}

export function getThumbnailCacheKey(
  src: string,
  timeInSeconds: number,
  width: number,
  height: number
): string {
  return `${src}-${timeInSeconds}-${width}-${height}`;
}

export function getVideoAspectRatio(width: number, height: number): number {
  return width / height;
}

export function calculateThumbnailDimensions(
  videoWidth: number,
  videoHeight: number,
  targetHeight: number
): { width: number; height: number } {
  const aspectRatio = getVideoAspectRatio(videoWidth, videoHeight);
  return {
    width: targetHeight * aspectRatio,
    height: targetHeight,
  };
}

export function getTotalContentWidth(thumbnails: ThumbnailMetadata[]): number {
  if (thumbnails.length === 0) return 0;
  const lastThumbnail = thumbnails[thumbnails.length - 1];
  return lastThumbnail.position + lastThumbnail.width;
}

export function isScrollableFilmstrip(
  thumbnails: ThumbnailMetadata[],
  containerWidth: number
): boolean {
  return getTotalContentWidth(thumbnails) > containerWidth;
}

export function timeToScrollPosition(
  timeInSeconds: number,
  thumbnails: ThumbnailMetadata[],
  containerWidth: number
): number {
  if (thumbnails.length === 0) return 0;

  // Find the thumbnail for this time
  const thumbnail = thumbnails.find(
    (t) => t.timeInSeconds === Math.floor(timeInSeconds)
  );
  if (!thumbnail) return 0;

  // Center the thumbnail in the viewport
  const scrollPosition =
    thumbnail.position - containerWidth / 2 + thumbnail.width / 2;
  return Math.max(0, scrollPosition);
}
